import { AUTH_ROUTE } from '../../features/auth/constants/auth-route.constants';
import type { AuthSessionState } from './auth-session.service';

export type AuthDestination = 'portal' | 'login' | 'link-account' | 'permission-revoked';

/**
 * Whether a URL is a portal page we may send a visitor back to after login.
 *
 * The portal used to live under `/app`, so "is this a portal page" was a prefix test. Now that it
 * sits at the root that test would accept anything, so this states the property it was really
 * carrying: a same-origin absolute path that is not the auth flow itself. A protocol-relative
 * `//host` is rejected because it leaves the origin entirely — without that check a crafted
 * `returnUrl` would turn login into an open redirect.
 */
export function isPortalUrl(url: string | null | undefined): url is string {
  return !!url && url.startsWith('/') && !url.startsWith('//') && !url.startsWith('/login');
}

export const AUTH_DESTINATION_ROUTE: Record<AuthDestination, string> = {
  portal: AUTH_ROUTE.portalHome,
  login: AUTH_ROUTE.login,
  'link-account': AUTH_ROUTE.linkAccount,
  'permission-revoked': AUTH_ROUTE.permissionRevoked
};

/**
 * The single source of truth for "where does this user belong right now" — guards and the
 * routing pipeline both key off this instead of re-deriving it from raw session state.
 *
 * `registrationActive` breaks the tie for a still-unlinked Firebase user (403/404): the
 * registration flow already owns that screen sequence, so the guard must let it be rather than
 * redirect to the link-account page it isn't on.
 */
export function resolveAuthDestination(
  state: AuthSessionState | undefined,
  registrationActive: boolean
): AuthDestination | 'pending' {
  if (!state) {
    return 'pending';
  }
  if (!state.user) {
    return 'login';
  }
  if (!state.isLoaded) {
    return 'pending';
  }
  if (state.error === null) {
    return state.person ? 'portal' : 'login';
  }
  if (state.error === 401) {
    return 'permission-revoked';
  }
  if (state.error === 403 || state.error === 404) {
    return registrationActive ? 'login' : 'link-account';
  }
  return 'login';
}
